import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Switch,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { toast } from "react-toastify";

import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  getClinicianProfile,
  getClinicianTimeSlots,
  updateTimeSlots,
  createTimeSlot,
  deleteTimeSlot,
} from "../../../api/services/api";
import { SlotBox } from "../../../components/SlotBox";

function SlotManagement() {
  // const id = useParams();
  const queryClient = useQueryClient();
  const [openIndexes, setOpenIndexes] = useState<any>([]);
  const [openSlotDialog, setOpenSlotDialog] = useState(false);
  const [slotForm, setSlotForm] = useState({
    dayOfWeek: "MONDAY",
    timeOfDay: "MORNING",
    startAt: "",
    endAt: "",
  });

  const { data: profileData } = useQuery(
    ["clinician-profile"],
    getClinicianProfile
  );

  const { data } = useQuery(
    ["timeSlots", profileData?.data?.id],
    getClinicianTimeSlots
  );

  let clinicianId = data?.data?.clinicianId;

  const { mutate } = useMutation(updateTimeSlots, {
    onSuccess: () => {
      queryClient.invalidateQueries("timeSlots");
    },
  });

  const { mutate: createSlotMutation, isLoading: isCreating } = useMutation(
    createTimeSlot,
    {
      onSuccess: () => {
        toast.success("Time slot created successfully!");
        queryClient.invalidateQueries("timeSlots");
        setOpenSlotDialog(false);
        setSlotForm({
          dayOfWeek: "MONDAY",
          timeOfDay: "MORNING",
          startAt: "",
          endAt: "",
        });
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || "Failed to create time slot");
      },
    }
  );

  const { mutate: deleteSlotMutation } = useMutation(deleteTimeSlot, {
    onSuccess: () => {
      toast.success("Time slot deleted successfully!");
      queryClient.invalidateQueries("timeSlots");
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || "Failed to delete time slot");
    },
  });

  const handleDayChange = (newItem: any) => {
    mutate({ ...newItem, clinicianId, status: !newItem.status });
  };

  const handleChange = (newItem: any) => {
    mutate({ ...newItem, status: !newItem.status });
  };

  const handleClick = (index: any) => {
    setOpenIndexes((prevOpenIndexes: any) => {
      const isOpen = prevOpenIndexes.includes(index);
      return isOpen
        ? prevOpenIndexes.filter((i: any) => i !== index)
        : [...prevOpenIndexes, index];
    });
  };

  const handleCreateSlot = () => {
    if (!slotForm.startAt || !slotForm.endAt) {
      toast.error("Please fill all required fields");
      return;
    }
    createSlotMutation({
      clinicianId,
      dayOfWeek: slotForm.dayOfWeek,
      timeOfDay: slotForm.timeOfDay,
      startAt: slotForm.startAt,
      endAt: slotForm.endAt,
    });
  };

  const handleDeleteSlot = (slotId: string) => {
    if (window.confirm("Are you sure you want to delete this time slot?")) {
      deleteSlotMutation(parseInt(slotId));
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="flex-end" mb={2}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenSlotDialog(true)}
        >
          Add Time Slot
        </Button>
      </Box>
      <Box
        border="1px solid #00000033"
        width="100%"
        borderRadius="10px 10px 0 0"
      >
        {data?.data?.days?.map((item: any, index: any) => (
          <>
            <Box>
              <Box
                sx={{
                  paddingX: "20px",
                  backgroundColor: "#0052CC08",
                  height: "60px",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  gap={2}
                  sx={{ width: "15%" }}
                >
                  <Typography
                    sx={{
                      color: item?.status
                        ? "rgba(18, 18, 18, 1)"
                        : "rgba(18, 18, 18, 0.4)",
                      flex: 1,
                    }}
                  >
                    {item?.day}
                  </Typography>
                  <Switch
                    checked={item?.status}
                    onChange={() => handleDayChange(item)}
                  />
                </Box>
                <IconButton onClick={() => handleClick(index)}>
                  {openIndexes.includes(index) ? (
                    <ExpandLess />
                  ) : (
                    <ExpandMore />
                  )}
                </IconButton>
              </Box>

              <Collapse
                in={openIndexes.includes(index)}
                timeout="auto"
                unmountOnExit
              >
                {item?.timeOfDay?.map((itemLabel: any) => (
                  <Box p="30px">
                    <Typography mb={2}>{itemLabel?.label}</Typography>

                    <Grid container spacing={2}>
                      {itemLabel?.slots?.map((slot: any) => (
                        <Grid item lg={4} key={slot.id}>
                          <Box position="relative">
                            <SlotBox
                              checked={slot?.status}
                              onChange={() => handleChange(slot)}
                              startAt={slot?.startAt}
                              endAt={slot?.endAt}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteSlot(slot.id)}
                              sx={{
                                position: "absolute",
                                top: 5,
                                right: 5,
                                bgcolor: "background.paper",
                                "&:hover": { bgcolor: "error.light", color: "white" },
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}
              </Collapse>
            </Box>
          </>
        ))}
      </Box>

      {/* Create Time Slot Dialog */}
      <Dialog open={openSlotDialog} onClose={() => setOpenSlotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Time Slot</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={2}>
            <FormControl fullWidth>
              <InputLabel>Day of Week</InputLabel>
              <Select
                value={slotForm.dayOfWeek}
                onChange={(e) => setSlotForm({ ...slotForm, dayOfWeek: e.target.value })}
                label="Day of Week"
              >
                <MenuItem value="MONDAY">Monday</MenuItem>
                <MenuItem value="TUESDAY">Tuesday</MenuItem>
                <MenuItem value="WEDNESDAY">Wednesday</MenuItem>
                <MenuItem value="THURSDAY">Thursday</MenuItem>
                <MenuItem value="FRIDAY">Friday</MenuItem>
                <MenuItem value="SATURDAY">Saturday</MenuItem>
                <MenuItem value="SUNDAY">Sunday</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Time of Day</InputLabel>
              <Select
                value={slotForm.timeOfDay}
                onChange={(e) => setSlotForm({ ...slotForm, timeOfDay: e.target.value })}
                label="Time of Day"
              >
                <MenuItem value="MORNING">Morning</MenuItem>
                <MenuItem value="AFTERNOON">Afternoon</MenuItem>
                <MenuItem value="EVENING">Evening</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Start Time"
              type="time"
              value={slotForm.startAt}
              onChange={(e) => setSlotForm({ ...slotForm, startAt: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Time"
              type="time"
              value={slotForm.endAt}
              onChange={(e) => setSlotForm({ ...slotForm, endAt: e.target.value })}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSlotDialog(false)}>Cancel</Button>
          <Button onClick={handleCreateSlot} variant="contained" disabled={isCreating}>
            {isCreating ? "Creating..." : "Add Slot"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default SlotManagement;
